import {
  Box,
  BoxProps,
  Button,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  VStack,
} from '@elkaso-app/web-design';
import { CmsCatalogCategoryDto } from 'apis/cms-catalog-categories';
import { useUpdateDealProductCategoryApi } from 'apis/exclusive-deals';
import { useEffect, useState } from 'react';
import { FiArrowLeft } from 'react-icons/fi';
import { CascadeCategorySelect } from './cascade-category-select';

const LEAF_DEPTHS = [3, 4];

export interface IEditProductCategoryModalCell extends BoxProps {
  trigger: JSX.Element;
  deal: any;
}

export const EditProductCategoryModal = ({ trigger, deal }: IEditProductCategoryModalCell) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedPath, setSelectedPath] = useState<CmsCatalogCategoryDto[]>([]);
  const { mutate: updateCategory, isLoading } = useUpdateDealProductCategoryApi({
    onSuccess: () => {
      onClose();
      setSelectedPath([]);
    },
  });

  // Reset cascade state whenever the modal is reopened — per design spec, pre-population is out of scope.
  useEffect(() => {
    if (!isOpen) {
      setSelectedPath([]);
    }
  }, [isOpen]);

  const leaf = selectedPath[selectedPath.length - 1];
  const isValidLeaf = !!leaf && LEAF_DEPTHS.includes(leaf.depth);

  const handleSubmit = () => {
    if (!isValidLeaf || !leaf) return;
    updateCategory({ dealId: deal.id, body: { catalogCategoryId: leaf.id } });
  };

  // Current assignment is not exposed by BE as a denormalized path here.
  // Show whichever compact signal is available so the admin has context.
  const currentCategoryHint =
    deal?.product?.categoryTitle ?? (deal?.catalogCategoryId ? `Current category id: ${deal.catalogCategoryId}` : null);

  return (
    <>
      <Box onClick={onOpen} display='inline-flex'>
        {trigger}
      </Box>

      <Modal isOpen={isOpen} onClose={onClose} size='xl' scrollBehavior='inside'>
        <ModalOverlay />
        <ModalContent maxW='1080px'>
          <ModalHeader>
            <VStack align='start' spacing='xs'>
              <Text>Edit Product Category</Text>
              {currentCategoryHint && (
                <Text fontSize='sm' fontWeight='normal' color='gray.500'>
                  {currentCategoryHint}
                </Text>
              )}
            </VStack>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <VStack align='stretch' spacing='sm'>
              <HStack justify='space-between' minH='28px'>
                <Button
                  variant='ghost'
                  size='xs'
                  leftIcon={<Icon as={FiArrowLeft} />}
                  onClick={() => setSelectedPath(selectedPath.slice(0, -1))}
                  isDisabled={selectedPath.length === 0}>
                  Back
                </Button>
                {selectedPath.length > 0 && (
                  <Button
                    variant='link'
                    size='xs'
                    colorScheme='gray'
                    onClick={() => setSelectedPath([])}>
                    Reset
                  </Button>
                )}
              </HStack>
              <CascadeCategorySelect selectedPath={selectedPath} onSelect={setSelectedPath} />
            </VStack>
          </ModalBody>

          <ModalFooter>
            <HStack width='100%' justify='space-between' align='center' spacing='md'>
              <VStack align='start' spacing={0} flex='1' minW={0}>
                {selectedPath.length > 0 ? (
                  <HStack spacing={0} wrap='wrap' maxW='600px'>
                    {selectedPath.map((p, i) => {
                      const label = p.title ?? `Untitled (${p.id})`;
                      const isLast = i === selectedPath.length - 1;
                      return (
                        <HStack key={p.id} spacing={0}>
                          <Text
                            as='button'
                            type='button'
                            onClick={() => setSelectedPath(selectedPath.slice(0, i + 1))}
                            fontSize='sm'
                            fontWeight={isLast ? 'semibold' : 'normal'}
                            color={isLast ? 'gray.800' : 'gray.600'}
                            _hover={isLast ? undefined : { textDecoration: 'underline', color: 'red.500' }}
                            cursor={isLast ? 'default' : 'pointer'}>
                            {label}
                          </Text>
                          {!isLast && (
                            <Text as='span' fontSize='sm' color='gray.400' mx='xs'>
                              ›
                            </Text>
                          )}
                        </HStack>
                      );
                    })}
                  </HStack>
                ) : (
                  <Text fontSize='sm' color='gray.400'>
                    Pick a category to see the path here.
                  </Text>
                )}
                {selectedPath.length > 0 && !isValidLeaf && (
                  <Text fontSize='xs' color='gray.500'>
                    Pick a category at depth 3 or 4 to save.
                  </Text>
                )}
              </VStack>

              <HStack spacing='sm'>
                <Button variant='outline' colorScheme='red' onClick={onClose} isDisabled={isLoading}>
                  Cancel
                </Button>
                <Button
                  variant='solid'
                  colorScheme='red'
                  onClick={handleSubmit}
                  isDisabled={!isValidLeaf || isLoading}
                  isLoading={isLoading}>
                  Save
                </Button>
              </HStack>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
